import type { ComponentProps } from 'react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { type DocumentRelatedContactSuggestionContact } from 'services/API/AIInteractionAPI';
import { formatCompanyName } from 'utils/format';

const contactContainerBase = {
  position: 'relative',
  width: '100%',
  padding: 'padding_8',
  borderRadius: '4px',
  cursor: 'pointer',
  boxSizing: 'border-box' as const,
  border: '2px solid transparent',
  backgroundColor: Colors.ArmyGreen100,
};

const itemContainer = {
  gap: 'padding_4',
};

const emptyStateStyle = {
  fontStyle: 'italic',
  color: Colors.TextQuiet,
};

const listStyle = {
  gap: 'padding_8',
  width: '100%',
};

const EmptyState = ({ label }: { label: string }) => (
  <Text sx={emptyStateStyle}>{label} not found</Text>
);

const contactItemStyle = {
  gap: 'padding_8',
  justifyContent: 'flex-start',
};

type IconName = ComponentProps<typeof Icon>['name'];

const ContactItem = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string;
  icon: IconName;
}) => (
  <FlexRow sx={contactItemStyle}>
    <Icon name={icon} fill={Colors.TextQuiet} />
    {value ? <Text.BodyBold>{value}</Text.BodyBold> : <EmptyState label={label} />}
  </FlexRow>
);

const ContactsFromAI = ({
  contacts,
}: {
  contacts: DocumentRelatedContactSuggestionContact[];
}) => {
  return (
    <FlexColumn sx={listStyle}>
      {contacts.map((contact) => {
        return (
          <FlexRow
            sx={contactContainerBase}
            key={`${contact.fullName}-${contact.email}-${contact.phone}-${contact.company}`}
            role="button"
            tabIndex={0}
          >
            <FlexColumn sx={itemContainer}>
              <ContactItem
                label="Company"
                value={contact.company ? formatCompanyName(contact.company) : undefined}
                icon={Icon.Name.CompanyBuilding}
              />
              <ContactItem label="Name" value={contact.fullName} icon={Icon.Name.User} />
              <ContactItem label="Email" value={contact.email} icon={Icon.Name.Email} />
              <ContactItem label="Phone" value={contact.phone} icon={Icon.Name.Phone} />
            </FlexColumn>
          </FlexRow>
        );
      })}
    </FlexColumn>
  );
};

export default ContactsFromAI;
